import PropTypes from "prop-types";
import * as React from "react";
import { useInfluencer, useShopImages } from "../../../hooks";
import {
  Button,
  CameraIcon,
  ImageIcon,
  Skeleton,
  TuneIcon,
  Typography,
  VisibilityIcon,
} from "../../../themeV2";
import CharacterStack from "../../CharacterStack/CharacterStack";
import GlassPill from "../../GlassPill/GlassPill";
import ImageAvatar from "../../ImageAvatar";
import VideoAvatarEditor from "../../VideoAvatarEditor";
import Description from "./Description";
import css from "./ShopHero.module.css";
import ShopStatus from "./ShopStatus";

const ShopBuilderHeader = React.memo(function ShopBuilderHeader({
  route,
  onEditImages,
  setEditingSettings,
}) {
  const { influencer, isLoading, isError } = useInfluencer({
    route,
    forShopBuilder: true,
  });

  const { images } = useShopImages(influencer?.influencerId);
  const headshotSrc = images.headshot?.squareUrl ?? influencer?.avatarUrl;
  const bannerUrl = images.banner?.url ?? influencer?.bannerUrl;

  const editAvatar = React.useCallback(() => {
    onEditImages("headshot");
  }, [onEditImages]);

  const avatarBlock = (
    <div className={css.avatarRelative}>
      <button
        type="button"
        className={css.shopAvatarButton}
        onClick={editAvatar}
        aria-label="Edit shop images"
      >
        <span className={css.shopAvatarCompact}>
          {headshotSrc ? (
            <img src={headshotSrc} alt="" />
          ) : (
            <ImageAvatar
              disableShadow
              influencer={influencer}
              height={80}
              width={80}
            />
          )}
        </span>
        <CharacterStack
          className={css.avatarStack}
          characters={images.character}
          size="sm"
        />
      </button>
      <GlassPill
        className={css.avatarEditPill}
        onClick={editAvatar}
        aria-label="Edit shop images"
      >
        <CameraIcon />
      </GlassPill>
    </div>
  );

  const nameBlock = (
    <Typography variant="headline-md" as="h2" clamp={2} className={css.name}>
      {influencer?.name ?? <Skeleton style={{ width: 200 }} />}
    </Typography>
  );

  const controlsBlock = (
    <div className={css.controls}>
      <ShopStatus route={route} />

      <Button
        variant="tonal"
        leading={<TuneIcon />}
        onClick={() => setEditingSettings(true)}
      >
        Shop settings
      </Button>

      {influencer?.route && (
        <Button
          as="a"
          variant="filled"
          leading={<VisibilityIcon />}
          href={`/${influencer.route}?preview`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Preview storefront
        </Button>
      )}
    </div>
  );

  return (
    <section className={css.shopHero}>
      {/* ── Banner ── */}
      <div
        className={css.shopBanner}
        style={bannerUrl ? { backgroundImage: `url(${bannerUrl})` } : undefined}
      >
        <div className={css.bannerScrim} />
        {Boolean(influencer) && (
          <GlassPill
            className={css.bannerEditPill}
            onClick={() => onEditImages("banner")}
          >
            <ImageIcon aria-hidden="true" /> Edit banner
          </GlassPill>
        )}
      </div>

      {/* ── Body ── */}
      <div className={css.shopBodyTwoCol}>
        <div className={css.contentRow}>
          <div className={css.videoColumn}>
            {influencer ? (
              <VideoAvatarEditor influencerId={influencer.influencerId} />
            ) : (
              <Skeleton className={css.videoSkeleton} />
            )}
          </div>
          <div className={css.detailsColumn}>
            <div className={css.titleRow}>
              {avatarBlock}
              {nameBlock}
            </div>
            <Description
              influencer={influencer}
              isLoading={isLoading}
              isError={isError}
              align="start"
            />
          </div>
        </div>
        {controlsBlock}
      </div>
    </section>
  );
});

ShopBuilderHeader.propTypes = {
  route: PropTypes.string,
  onEditImages: PropTypes.func.isRequired,
  setEditingSettings: PropTypes.func,
};

export default ShopBuilderHeader;
